"use client";

/**
 * Global Modal Stack Context
 * 
 * Provides the React Context and hook for accessing the global modal stack.
 */

import * as React from "react";
import type { GlobalModalStackContextType } from "./types";

/** React Context for the global modal stack */
export const GlobalModalStackContext = React.createContext<GlobalModalStackContextType | null>(null);

/**
 * Hook to access the global modal stack
 * 
 * Must be used within a GlobalModalStackProvider
 * 
 * @returns The global modal stack context
 * @throws Error if used outside of GlobalModalStackProvider
 * 
 * @example
 * const { openModal, closeModal, isModalOpen } = useGlobalModalStack();
 * 
 * // Open a modal
 * openModal("edit-contact", { contactId: "123" });
 * 
 * // Check if modal is open
 * if (isModalOpen("edit-contact")) {
 *   // Modal is open
 * }
 * 
 * // Close a modal
 * closeModal("edit-contact");
 */
export const useGlobalModalStack = () => {
  const context = React.useContext(GlobalModalStackContext);
  if (!context) {
    throw new Error('useGlobalModalStack must be used within a GlobalModalStackProvider');
  }
  return context;
};
